import {
	Component,
	ElementRef,
	EventEmitter,
	Output,
	ViewChild,
} from '@angular/core';
import { MatStepper, MatStep, MatStepLabel } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import {
	SelectedUserEvent,
	UserType,
} from 'src/app/components/user-search-bar/user-search-bar.component';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { User } from 'src/app/models/data/user';
import { ApiService, QueryParam } from 'src/app/services/api.services';
import { AuthService } from 'src/app/services/auth.service';
import { NavigationHistoryService } from 'src/app/services/navigation-history.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { EditRole } from '../../add-role/add-role-main-tab.component';
import { AddRoleUsersComponent } from '../../add-role/add-role-users/add-role-users.component';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { UserExistsSnackbarComponent } from '../../../../components/user-exists-snackbar/user-exists-snackbar.component';
import { NotAuthorizedNoticeComponent } from '../../../../components/not-authorized-notice/not-authorized-notice.component';
import { AppNavigationHeaderComponent } from '../../../../components/navigation-header/app-navigation-header.component';
import { MatButton } from '@angular/material/button';
import { RoleDetailConfirmationComponent } from '../role-detail-confirmation/role-detail-confirmation.component';

enum StepperPage {
	addUser = 0,
	confirmation = 1,
}

@Component({
	selector: 'app-role-detail-add-user',
	templateUrl: './role-detail-add-user-main-tab.component.html',
	styleUrls: [
		'./role-detail-add-user-main-tab.component.scss',
		'../../../common/main.scss',
	],
	imports: [
		NotAuthorizedNoticeComponent,
		AppNavigationHeaderComponent,
		MatButton,
		MatStepper,
		MatStep,
		MatStepLabel,
		AddRoleUsersComponent,
		RoleDetailConfirmationComponent,
	],
})
export class RoleDetailAddUserComponent {
	@Output() updateRoleRequestStatusEvent =
		new EventEmitter<AssignUserRoleRequestStatusEvent>();

	roleId = '';
	canEditUsers: boolean;
	companyId: string;

	editRole: EditRole = {
		id: '',
		name: '',
		users: [],
		systemCapabilityItems: [],
	};

	isActiveCompany = false;
	authSubscription: any;
	isEditable = false;

	@ViewChild('stepperRef') stepperRef!: ElementRef;
	@ViewChild(AddRoleUsersComponent) addRoleUsers?: AddRoleUsersComponent;
	snackBarRef?: MatSnackBarRef<UserExistsSnackbarComponent>;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly apiService: ApiService,
		private readonly router: Router,
		private readonly scService: SystemCapabilityService,
		private readonly navHistory: NavigationHistoryService,
		private readonly authService: AuthService,
		private snackBar: MatSnackBar
	) {
		this.roleId = this.route.snapshot.paramMap.get('id') ?? '';
		this.companyId =
			this.router.lastSuccessfulNavigation?.extras?.state?.companyId ?? '';
		this.canEditUsers = scService.has(SystemCapabilityEnum.userRoleManagement);

		if (!this.companyId) {
			console.error('No company id found; navigating back');
			navHistory.navigateBack();
		}

		this.authSubscription = this.authService.userSession().subscribe((it) => {
			const activeCompanyId =
				it.assignedRole?.entityRelationship?.parentEntityId;
			this.isActiveCompany = activeCompanyId === this.companyId;
		});
	}

	async moveNext(stepper: MatStepper): Promise<void> {
		const users = await Promise.all(
			this.editRole?.users?.map(async (user) => {
				if (!user?.user?.id) {
					const email = user?.user?.email?.trim()?.toLowerCase();
					const queryParameters: QueryParam[] = [
						{ param: 'email', value: email },
						{ param: 'rowIndex', value: 0 },
						{ param: 'rowCount', value: 100 },
					];
					const fetchedUsers =
						await this.apiService.getAllUsers(queryParameters);
					return !!fetchedUsers?.find(
						(usr) => usr?.email?.trim()?.toLowerCase() === email
					);
				}
				return false;
			})
		);
		if (users?.some(Boolean)) {
			this.snackBarRef = this.snackBar.openFromComponent(
				UserExistsSnackbarComponent,
				{
					duration: 3000,
					panelClass: 'existing-user-snackbar',
				}
			);
			return;
		}

		this.addRoleUsers?.checkForExistingRolesThenContinue(() => {
			this.addUsers();
			stepper.next();
		}, this.companyId);
	}

	movePrevious(): void {
		this.navHistory.navigateBack();
	}

	isValid(): boolean {
		return this.addRoleUsers?.isValid ?? false;
	}

	async addUsers(): Promise<void> {
		this.updateRoleRequestStatusEvent.emit({
			status: AssignUserRoleRequestStatus.loading,
		});

		const { role } = await this.apiService.getRoleById(this.roleId);
		try {
			const users = this.editRole.users.map((it) => it.user) ?? [];

			const userIds =
				this.editRole.users
					.filter((it) => it.userType === UserType.existingUser)
					.map((it) => it.user.id) ?? [];

			if (userIds.length > 0) {
				await this.apiService.assignRoleToUserId(
					userIds,
					role.id,
					this.companyId
				);
			}

			const newUsers =
				this.editRole.users
					.filter((it) => it.userType === UserType.newUser)
					.map((it) => it.user) ?? [];

			if (newUsers.length > 0) {
				const newUserResponse = await this.apiService.registerNewUser(
					newUsers,
					this.companyId,
					role.id
				);
				const newUserIds = newUserResponse.data.userIds;
				userIds.push(...newUserIds);
			}

			if (userIds.length > 0) {
				this.updateRoleRequestStatusEvent.emit({
					status: AssignUserRoleRequestStatus.success,
					data: { roleName: role.name, users },
				});
			}
		} catch (error) {
			this.updateRoleRequestStatusEvent.emit({
				status: AssignUserRoleRequestStatus.failure,
				data: { roleName: role.name, users: [] },
			});
		}
	}
}

export interface SelectedUsersEvent {
	selectedUsers: SelectedUserEvent[];
}

export enum AssignUserRoleRequestStatus {
	loading,
	success,
	failure,
}

export interface AssignUserRoleRequestStatusEvent {
	status: AssignUserRoleRequestStatus;
	data?: { roleName?: string; users: User[] };
}
